/**
 * Dev-only long-task instrument (docs/preload-design.md §2.7).
 *
 * `new PerformanceObserver('longtask')` → ring buffer of
 * `{ startTime, duration, phase }` where phase is 'boot' | 'session'. Exposed as
 * `window.__longtasks` for the screenshot harness and merged into
 * `Engine.perfSnapshot()`. Enabled when `import.meta.env.DEV` or `?perf=1`.
 *
 * Note (MEMORY): headless SwiftShader timings are meaningless for GPU-bound
 * numbers — longtask counts are still valid for CPU stalls, but sign-off
 * numbers come from real hardware.
 */

export interface LongTaskEntry {
  startTime: number;
  duration: number;
  phase: 'boot' | 'session';
}

/** Ring capacity. Long tasks are (supposed to be) rare; 200 covers a session. */
const CAP = 200;

/** True when the dev perf instruments (longtask log, program census) should run. */
export function devPerfEnabled(): boolean {
  if (import.meta.env.DEV) return true;
  return typeof location !== 'undefined' && /[?&]perf=1\b/.test(location.search);
}

export class LongTaskLog {
  private readonly entries: LongTaskEntry[] = [];
  private obs: PerformanceObserver | null = null;
  private phase: 'boot' | 'session' = 'boot';
  private boots = 0;
  private sessions = 0;
  private worst = 0;

  get active(): boolean { return this.obs !== null; }
  get count(): number { return this.boots + this.sessions; }
  get bootCount(): number { return this.boots; }
  get sessionCount(): number { return this.sessions; }
  get worstMs(): number { return this.worst; }

  /** Idempotent. Silently a no-op where the 'longtask' entry type is missing. */
  start(): void {
    if (this.obs !== null || typeof PerformanceObserver === 'undefined') return;
    try {
      const obs = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) this.record(e.startTime, e.duration);
      });
      // `buffered` back-fills tasks that landed before the observer existed
      // (script parse, WASM compile) into the 'boot' phase.
      obs.observe({ type: 'longtask', buffered: true });
      this.obs = obs;
    } catch {
      this.obs = null;
      return;
    }
    (window as unknown as { __longtasks: LongTaskEntry[] }).__longtasks = this.entries;
  }

  /** Called once when `loading.done()` is imminent: later tasks are gameplay stalls. */
  markSession(): void { this.phase = 'session'; }

  dispose(): void {
    this.obs?.disconnect();
    this.obs = null;
  }

  private record(startTime: number, duration: number): void {
    if (duration > this.worst) this.worst = duration;
    if (this.phase === 'boot') this.boots++;
    else this.sessions++;
    // Cold path (long tasks are the thing being hunted); shift() is fine here.
    if (this.entries.length >= CAP) this.entries.shift();
    this.entries.push({ startTime, duration, phase: this.phase });
  }
}
